import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Headphones,
  Truck,
  Wrench,
  Shield,
  Clock,
  MapPin,
  DollarSign,
  Home,
  CheckCircle2,
} from "lucide-react";
import { InnerPage } from "@/components/InnerPage";
import { SectionHeader } from "@/components/SectionHeader";
import { pageHead } from "@/lib/seo";
import { breadcrumbsFor } from "@/lib/schema";
import { SITE } from "@/lib/site";

const PATH = "/drive-with-us";

const META_DESCRIPTION =
  "CDL-A driving jobs with a family-run NJ carrier since 2012. Coast-to-coast lanes and 24/7 dispatch support. Apply with Fox Trucking.";

/*
 * TODO: JobPosting JSON-LD intentionally omitted.
 * Google JobPosting requires accurate title, description, datePosted,
 * hiringOrganization, and jobLocation — plus we should not invent baseSalary,
 * employmentType, experienceRequirements, or validThrough. Add JobPosting only
 * after Fox Trucking confirms: job title, posting date, employment type
 * (FULL_TIME etc.), experience requirements, pay (baseSalary), home-time
 * policy, and whether owner-operators are hired.
 */

export const Route = createFileRoute("/drive-with-us")({
  head: () =>
    pageHead({
      title: "CDL Driver Jobs in NJ | Drive With Fox Trucking",
      description: META_DESCRIPTION,
      path: PATH,
      ogTitle: "CDL Driver Jobs in NJ | Drive With Fox Trucking",
      ogDescription: META_DESCRIPTION,
    }),
  component: DriveWithUsPage,
});

const BENEFITS = [
  {
    i: Headphones,
    t: "Dispatch that picks up",
    d: "Real people on the phone — 24/7 support when you need a decision, not a ticket number.",
  },
  {
    i: Truck,
    t: "Steady freight",
    d: "Produce, reefer, dry goods, and general freight moving coast to coast out of New Jersey.",
  },
  {
    i: Wrench,
    t: "Well-maintained equipment",
    d: "Trucks and trailers kept ready for the road so you spend time driving, not waiting on repairs.",
  },
  {
    i: Shield,
    t: "Respect on every load",
    d: "A family-run carrier since 2012 — we treat drivers like partners, not numbers on a board.",
  },
  {
    i: MapPin,
    t: "Coast-to-coast lanes",
    d: "Long-haul work across the lower 48 with a New Jersey home base.",
  },
  {
    i: Clock,
    t: "Fast follow-up",
    d: "Apply today — we aim to respond within 24–48 hours after reviewing your background.",
  },
] as const;

const DRIVER_FAQS = [
  {
    question: "Do you hire owner-operators?",
    // TODO: Confirm whether Fox Trucking currently hires owner-operators.
    answer:
      "[OWNER-OPERATOR HIRING POLICY — confirm with Fox Trucking]. Call recruiting at " +
      SITE.phoneDisplay +
      " for the latest openings.",
  },
  {
    question: "What lanes do you run?",
    answer:
      "We haul produce, dry goods, and general freight coast to coast from our New Jersey base across the lower 48.",
  },
  {
    question: "How often am I home?",
    // TODO: Confirm home-time policy with Fox Trucking.
    answer: "[HOME TIME POLICY — confirm days out / days home with Fox Trucking].",
  },
  {
    question: "What experience do I need?",
    // TODO: Confirm CDL class, years of experience, and MVR requirements.
    answer:
      "[CDL CLASS, YEARS OF EXPERIENCE, MVR/CLEAN RECORD REQUIREMENTS — confirm with recruiting].",
  },
  {
    question: "How do I apply?",
    answer:
      "Fill out the application form on this page or call recruiting at " +
      SITE.phoneDisplay +
      ". We typically follow up within 24–48 hours.",
  },
];

function DriveWithUsPage() {
  return (
    <InnerPage
      hero={{
        eyebrow: "Class A CDL · New Jersey",
        title: "Drive With Fox Trucking",
        intro:
          "Join a family-run NJ carrier where dispatch picks up the phone. Coast-to-coast freight since 2012 — respectful support, steady work, and people who have your back.",
        primaryCta: { label: "Apply Now", href: "#apply" },
        secondaryCta: { label: "Call Recruiting", href: `tel:${SITE.phoneTel}` },
        showPhoneCta: false,
      }}
      breadcrumbs={breadcrumbsFor("Drive With Us", PATH)}
      faqs={DRIVER_FAQS}
      faqTitle="Driver questions, answered."
      faqSubtitle="Straight answers about lanes, home time, and how to apply."
      bottomCta={{
        title: "Ready to drive with Fox?",
        subtitle: "Apply online or call recruiting — real people, fast follow-up.",
        bookLabel: "Apply Now",
        bookHref: "#apply",
      }}
    >
      {/* WHY DRIVERS CHOOSE US */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Why Fox"
            title="Why drivers choose us."
            subtitle="A carrier built around communication, reliable freight, and equipment that stays on the road."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <div
                key={b.t}
                className="group relative overflow-hidden rounded-lg border border-border bg-card p-8 transition hover:border-primary"
              >
                <b.i className="h-10 w-10 text-primary" aria-hidden="true" />
                <h3 className="mt-6 font-display text-2xl text-card-foreground">{b.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAY AND HOME TIME */}
      <section className="bg-muted py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Compensation"
            title="Pay and home time."
            subtitle="We publish exact numbers once recruiting confirms them — no invented rates on this page."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <PlaceholderCard
              icon={DollarSign}
              title="Pay structure"
              // TODO: Replace with confirmed cents-per-mile and/or percentage pay.
              body="[PAY STRUCTURE - cents per mile / percentage]"
            />
            <PlaceholderCard
              icon={Home}
              title="Home time"
              // TODO: Replace with confirmed home-time policy (days out / days home).
              body="[HOME TIME POLICY]"
            />
            <PlaceholderCard
              icon={CheckCircle2}
              title="Sign-on bonus"
              // TODO: Replace with confirmed sign-on bonus amount, or remove if none.
              body="[SIGN-ON BONUS IF ANY]"
            />
          </div>
        </div>
      </section>

      {/* EQUIPMENT */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Fleet"
            title="Equipment you’ll run."
            subtitle="Trailer mix covers reefer and dry van freight — truck and trailer details TBD."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <PlaceholderCard
              icon={Truck}
              title="Tractors"
              // TODO: Replace with confirmed truck makes/models and model years.
              body="[TRUCK MAKES/MODELS]"
            />
            <PlaceholderCard
              icon={Truck}
              title="Trailers"
              // TODO: Replace with confirmed trailer types and counts (reefer, dry van).
              body="[TRAILER TYPES - reefer, dry van]"
            />
          </div>
        </div>
      </section>

      {/* REQUIREMENTS */}
      <section className="relative bg-secondary py-24 text-secondary-foreground">
        <div className="absolute inset-0 bg-noise opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            tone="dark"
            eyebrow="Qualifications"
            title="What we’re looking for."
            subtitle="Requirements below are placeholders until recruiting confirms the official checklist."
          />
          <ul className="mt-10 max-w-3xl space-y-4">
            {/* TODO: Confirm CDL class requirement (expected Class A). */}
            <RequirementItem label="CDL" value="[CDL CLASS]" />
            {/* TODO: Confirm minimum years of OTR / commercial experience. */}
            <RequirementItem label="Experience" value="[YEARS OF EXPERIENCE]" />
            {/* TODO: Confirm MVR / clean record / violation thresholds. */}
            <RequirementItem label="Record" value="[MVR/CLEAN RECORD REQUIREMENTS]" />
          </ul>
          <p className="mt-8 max-w-2xl text-sm text-secondary-foreground/70">
            Not sure if you qualify? Call recruiting at{" "}
            <a
              href={`tel:${SITE.phoneTel}`}
              className="text-accent underline-offset-2 hover:underline"
            >
              {SITE.phoneDisplay}
            </a>{" "}
            — we’ll tell you straight.
          </p>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <DriverApplicationForm />
    </InnerPage>
  );
}

function PlaceholderCard({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-border border-dashed bg-card p-8">
      <Icon className="h-8 w-8 text-primary" aria-hidden="true" />
      <h3 className="mt-4 font-display text-xl text-card-foreground">{title}</h3>
      <p className="mt-2 font-mono text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function RequirementItem({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex flex-col gap-1 rounded-md border border-secondary-foreground/15 bg-secondary/50 px-5 py-4 sm:flex-row sm:items-center sm:gap-6">
      <span className="font-display text-xs tracking-widest text-accent">{label}</span>
      <span className="font-mono text-sm text-secondary-foreground/90">{value}</span>
    </li>
  );
}

function parseDriverApplication(raw: Record<string, FormDataEntryValue>) {
  const name = String(raw.name ?? "").trim();
  const phone = String(raw.phone ?? "").trim();
  const email = String(raw.email ?? "").trim();
  const cdlClass = String(raw.cdlClass ?? "").trim();
  const yearsExperience = String(raw.yearsExperience ?? "").trim();
  const position = String(raw.position ?? "").trim();
  const message = String(raw.message ?? "").trim();

  if (!name || name.length > 120) return { error: "Name required" };
  if (!phone || phone.length > 30) return { error: "Phone required" };
  if (!email || email.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: "Valid email required" };
  }
  if (!cdlClass) return { error: "CDL class required" };
  if (!yearsExperience || yearsExperience.length > 40) {
    return { error: "Years of experience required" };
  }
  if (position !== "company-driver" && position !== "owner-operator") {
    return { error: "Select a preferred position" };
  }
  if (message.length > 1500) return { error: "Message is too long" };

  return {
    data: { name, phone, email, cdlClass, yearsExperience, position, message },
  };
}

function DriverApplicationForm() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const parsed = parseDriverApplication(Object.fromEntries(new FormData(form)));
    if ("error" in parsed) {
      setSuccess(false);
      setStatus(parsed.error ?? "Please check the form");
      return;
    }

    setLoading(true);
    setSuccess(false);
    const d = parsed.data;
    const positionLabel = d.position === "owner-operator" ? "Owner-operator" : "Company driver";
    const details = [
      `CDL: ${d.cdlClass}`,
      `Experience: ${d.yearsExperience}`,
      `Position: ${positionLabel}`,
      d.message ? `Message: ${d.message}` : null,
    ]
      .filter(Boolean)
      .join("%0D%0A");
    const body = `${details}%0D%0A%0D%0A--%0D%0A${d.name}%0D%0A${d.email} · ${d.phone}`;
    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent(
      `Driver Application — ${d.name}`,
    )}&body=${body}`;
    setSuccess(true);
    setStatus(
      `Thanks, ${d.name}. Your email app is opening with this application ready — hit send, or call ${SITE.phoneDisplay}.`,
    );
    form.reset();
    setTimeout(() => setLoading(false), 600);
  }

  return (
    <section id="apply" className="scroll-mt-20 bg-muted py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeader
          eyebrow="Apply"
          title="Start your application."
          subtitle="Tell us who you are — we follow up within 24–48 hours. Prefer to talk? Call recruiting."
        />
        <div className="mt-14 mx-auto max-w-3xl">
          {success && status ? (
            <div
              className="rounded-lg border border-accent/40 bg-card p-8 text-center"
              role="status"
            >
              <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
              <h3 className="mt-4 font-display text-2xl text-card-foreground">Application ready</h3>
              <p className="mt-2 text-sm text-muted-foreground">{status}</p>
              <button
                type="button"
                className="mt-6 inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 font-display tracking-wider text-primary-foreground hover:bg-primary/90"
                onClick={() => {
                  setSuccess(false);
                  setStatus(null);
                }}
              >
                Submit another
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-lg border border-border bg-card p-8 shadow-sm"
              noValidate
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Full Name" name="name" required autoComplete="name" />
                <Field label="Phone" name="phone" type="tel" required autoComplete="tel" />
                <Field label="Email" name="email" type="email" required autoComplete="email" />
                <div>
                  <label
                    htmlFor="driver-cdlClass"
                    className="font-display text-xs tracking-widest text-muted-foreground"
                  >
                    CDL CLASS <span className="text-primary">*</span>
                  </label>
                  <select
                    id="driver-cdlClass"
                    name="cdlClass"
                    required
                    defaultValue=""
                    className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="" disabled>
                      Select CDL class
                    </option>
                    <option value="Class A">Class A</option>
                    <option value="Class B">Class B</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <Field
                  label="Years of Experience"
                  name="yearsExperience"
                  required
                  autoComplete="off"
                  placeholder="e.g. 5"
                />
                <div>
                  <label
                    htmlFor="driver-position"
                    className="font-display text-xs tracking-widest text-muted-foreground"
                  >
                    PREFERRED POSITION <span className="text-primary">*</span>
                  </label>
                  <select
                    id="driver-position"
                    name="position"
                    required
                    defaultValue=""
                    className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="" disabled>
                      Select position
                    </option>
                    <option value="company-driver">Company driver</option>
                    <option value="owner-operator">Owner-operator</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label
                  htmlFor="driver-message"
                  className="font-display text-xs tracking-widest text-muted-foreground"
                >
                  MESSAGE
                </label>
                <textarea
                  id="driver-message"
                  name="message"
                  rows={5}
                  maxLength={1500}
                  placeholder="Endorsements, preferred lanes, when you can start..."
                  autoComplete="off"
                  className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <button
                disabled={loading}
                type="submit"
                className="mt-6 w-full rounded-md bg-primary px-6 py-4 font-display tracking-widest text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {loading ? "Opening email..." : "Submit Application by Email"}
              </button>
              {status && !success && (
                <p className="mt-3 text-center text-sm text-primary" role="alert">
                  {status}
                </p>
              )}
              <p className="mt-3 text-center text-xs text-muted-foreground">
                This opens your email app with the details filled in. Prefer to talk? Call{" "}
                <a href={`tel:${SITE.phoneTel}`} className="underline-offset-2 hover:underline">
                  {SITE.phoneDisplay}
                </a>
                .
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  autoComplete = "off",
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
}) {
  const id = `driver-${name}`;
  return (
    <div>
      <label htmlFor={id} className="font-display text-xs tracking-widest text-muted-foreground">
        {label.toUpperCase()}
        {required && <span className="text-primary"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        name={name}
        required={required}
        maxLength={255}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className="mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  );
}
